import React, { useState, useMemo } from 'react';
import { PlusOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Tag, Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import ListPageHeader from '@shared/ListPageHeader';
import OrderPostProcessorConceptModal from '@shared/order_post_processors/OrderPostProcessorConceptModal';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { orderPostProcessorService } from '@services/admin/orderPostProcessorService';
import { MultipleValue, SelectValue } from '@shared/table/Rules/component';

interface OrderPostProcessorRow {
  id: string;
  name?: string;
  priority?: number;
  active?: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
  updated_at?: string;
  [key: string]: unknown;
}

export default function OrderPostProcessorsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const columns = useMemo(() => [
    {
      name: t('admin.pages.order_post_processors.columns.name', 'Name'),
      persist: true,
      sortable: true,
      sortFieldName: 'order_post_processors.name',
      minWidth: '200px',
      wrap: true,
      cell: ({ id, name }: OrderPostProcessorRow) => (
        <Link to={`/order_post_processors/${id}`}>
          {name}
        </Link>
      ),
    },
    {
      name: t('admin.pages.order_post_processors.columns.priority', 'Priority'),
      selector: (row: OrderPostProcessorRow) => row.priority,
      sortable: true,
      sortFieldName: 'order_post_processors.priority',
      width: '100px',
    },
    {
      name: t('admin.pages.order_post_processors.columns.active', 'Status'),
      width: '100px',
      cell: ({ active }: OrderPostProcessorRow) => (
        active
          ? <Tag color="success">{t('common.enabled', 'Enabled')}</Tag>
          : <Tag color="default">{t('common.disabled', 'Disabled')}</Tag>
      ),
    },
    {
      name: t('admin.pages.order_post_processors.columns.validity', 'Validity'),
      minWidth: '250px',
      cell: ({ start_datetime, end_datetime }: OrderPostProcessorRow) => {
        if (!start_datetime && !end_datetime) return t('common.always', 'Always');
        const start = start_datetime ? dayjs(start_datetime).format('YYYY-MM-DD HH:mm') : '∞';
        const end = end_datetime ? dayjs(end_datetime).format('YYYY-MM-DD HH:mm') : '∞';
        return `${start} ~ ${end}`;
      }
    },
    {
      name: t('common.updated_at', 'Updated At'),
      selector: (row: OrderPostProcessorRow) => row.updated_at ? dayjs(row.updated_at).format('YYYY-MM-DD HH:mm') : '-',
      sortable: true,
      sortFieldName: 'order_post_processors.updated_at',
      width: '160px',
    }
  ], [t]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('admin.pages.order_post_processors.filters.name', 'Name'),
      component: MultipleValue,
      inputName: 'name',
      defaultPinned: true,
      createable: true,
    },
    {
      label: t('admin.pages.order_post_processors.filters.active', 'Status'),
      component: SelectValue,
      inputName: 'active',
      defaultOptions: [
        { label: t('common.enabled', 'Enabled'), value: 'true' },
        { label: t('common.disabled', 'Disabled'), value: 'false' },
      ],
    }
  ], [t]);

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['orders', 'orders.post_processors']}
        guideButton={
          <Tooltip title={t('concepts.order_post_processors.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/order_post_processors/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [t('admin.pages.order_post_processors.columns.name', 'Name')],
        }}
        columnsEditable={true}
        tableId="AdminOrderPostProcessorsList"
        defaultQuery={{
          sorting_field: 'order_post_processors.priority',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={orderPostProcessorService}
      />
      <OrderPostProcessorConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}
